import { useLocalObservable } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';

import { CallMediaPermissionValue, IMediaPermissions } from '@gem/call/CallStore/Call.types';
import { permissions } from '@gem/call/Removable/tasks/permissions/permissions';
import { getDefaultDevices, IDevices } from '@gem/call/tasks/getDefaultDevices';
import { useTranslation } from '@gem/modules/i18n';
import { logger } from '@gem/modules/logger';
import { popupsTasks } from '@gem/popups';

import { IRoomCallPreviewVideoAudioSourcePresenter } from './RoomCallPreviewVideo.audioSource.presenter';
import { IRoomCallBackgroundVideoPresenter } from './RoomCallPreviewVideo.background.presenter';

export interface IRoomCallPreviewVideoPresenter {
  hasVideoPermission: boolean | undefined;
  hasAudioPermission: boolean | undefined;
  audioMuted: boolean;
  videoMuted: boolean;
  audioProcessing: boolean;
  videoProcessing: boolean;
  currentAudioInput: string | null;
  currentVideoInput: string | null;
  currentAudioOutput: string | null;
  videoStream: MediaStream | null;
  isBackgroundPanelShown: boolean;
  showBackgroundPanel(): void;
  hideBackgroundPanel(): void;
  initAudio(): Promise<void>;
  initVideo(): Promise<void>;
  setCurrentAudioInput(deviceId: string, needToPublish?: boolean): Promise<void>;
  setCurrentAudioOutput(deviceId: string, needToPublish?: boolean): Promise<void>;
  setCurrentVideoInput(deviceId: string, needToPublish?: boolean): Promise<void>;
  unmuteAudio(): Promise<void>;
  muteAudio(): Promise<void>;
  unmuteVideo(): Promise<void>;
  muteVideo(): void;
  dispose(): void;
  checkChangeDevices(): Promise<void>;
  onCallStart(): Promise<void>;
}

interface IRoomCallPreviewVideoPresenterProps {
  onChangeAudioMuted(value: boolean): void;
  onChangeVideoMuted(value: boolean): void;
  onMediaPermissionsChanged?(params: Partial<IMediaPermissions>): void;
  onChangeInitialDevicesIds?(props: IDevices): void;
  audioSourcePresenter: IRoomCallPreviewVideoAudioSourcePresenter;
  videoBackgroundPresenter: IRoomCallBackgroundVideoPresenter;
  initialMedia?: {
    isVideoMuted: boolean;
    isAudioMuted: boolean;
  };
}

export const useRoomCallPreviewVideoPresenter = (
  props: IRoomCallPreviewVideoPresenterProps
): IRoomCallPreviewVideoPresenter => {
  const audioStreamRef = useRef<MediaStream | null>(null);
  const { t } = useTranslation();
  const presenter = useLocalObservable<IRoomCallPreviewVideoPresenter>(() => ({
    videoStream: null,
    hasVideoPermission: undefined,
    hasAudioPermission: undefined,
    currentAudioInput: null,
    currentVideoInput: null,
    currentAudioOutput: null,
    audioMuted: props.initialMedia?.isAudioMuted || false,
    videoMuted: props.initialMedia?.isVideoMuted || false,
    audioProcessing: false,
    videoProcessing: false,
    isBackgroundPanelShown: false,

    showBackgroundPanel(): void {
      if (presenter.videoMuted) {
        popupsTasks.showAlert(t('call_background_camera_is_off_message'));

        return;
      }
      presenter.isBackgroundPanelShown = true;
    },

    hideBackgroundPanel(): void {
      presenter.isBackgroundPanelShown = false;
    },

    async unmuteAudio(): Promise<void> {
      try {
        const hasMicrophone = await permissions.hasMicrophone();

        if (!hasMicrophone) return;

        if (!presenter.hasAudioPermission) {
          presenter.hasAudioPermission = await permissions.getAudioPermission(false);
        }

        if (!presenter.hasAudioPermission || !presenter.audioMuted || presenter.audioProcessing) {
          presenter.audioProcessing = false;
          return;
        }
        presenter.audioProcessing = true;

        const audioStream = await navigator.mediaDevices.getUserMedia({
          audio: presenter.currentAudioInput ? { deviceId: presenter.currentAudioInput } : true,
        });

        audioStreamRef.current = audioStream;
        presenter.audioMuted = false;
        await props.audioSourcePresenter.checkAudioLevel(audioStream);

        props.onChangeAudioMuted(false);
        presenter.audioProcessing = false;
      } catch (e) {
        logger.get('RoomCallPreviewPopup.presenter').error('unmuteAudio', e);
      }
    },

    async muteAudio(): Promise<void> {
      if (presenter.audioMuted || presenter.audioProcessing) {
        return;
      }
      props.audioSourcePresenter.stopCheckAudioLevel();
      audioStreamRef.current?.getAudioTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
      presenter.audioMuted = true;
      props.onChangeAudioMuted(true);
    },

    async unmuteVideo(): Promise<void> {
      try {
        const hasCamera = await permissions.hasCamera();

        if (!hasCamera) return;

        if (!presenter.hasVideoPermission) {
          presenter.hasVideoPermission = await permissions.getVideoPermission(
            presenter.currentVideoInput || undefined,
            false
          );
        }

        if (!presenter.hasVideoPermission || !presenter.videoMuted || presenter.videoProcessing) {
          presenter.videoProcessing = false;
          return;
        }

        presenter.videoProcessing = true;

        presenter.videoStream = await navigator.mediaDevices.getUserMedia({
          video: presenter.currentVideoInput ? { deviceId: presenter.currentVideoInput } : true,
        });

        await props.videoBackgroundPresenter.addBackgroundToMediaTrack(presenter.videoStream);
        presenter.videoMuted = false;
        props.onChangeVideoMuted(false);
        presenter.videoProcessing = false;
      } catch (e) {
        logger.get('RoomCallPreviewPopup.presenter').error('unmuteVideo', e);
      }
    },

    async muteVideo(): Promise<void> {
      if (presenter.videoMuted || presenter.videoProcessing) {
        return;
      }
      props.videoBackgroundPresenter.destroyBackgroundProcessor();
      presenter.videoStream?.getVideoTracks().forEach((track) => track.stop());
      presenter.videoStream = null;

      presenter.videoMuted = true;
      props.onChangeVideoMuted(true);
    },

    async onCallStart(): Promise<void> {
      if (!presenter.videoMuted) {
        presenter.muteVideo();
      }
      if (!presenter.audioMuted) {
        presenter.muteAudio();
      }
      props.videoBackgroundPresenter.destroyBackgroundProcessor();
    },

    async setCurrentAudioInput(deviceId: string, needToPublish?: boolean): Promise<void> {
      presenter.currentAudioInput = deviceId;
      if (!presenter.audioMuted && needToPublish) {
        await presenter.muteAudio();
        await presenter.unmuteAudio();
      } else if (needToPublish) {
        await presenter.unmuteAudio();
      }
      if (props.onChangeInitialDevicesIds) {
        props.onChangeInitialDevicesIds({
          audiooutput: presenter.currentAudioOutput,
          audioinput: presenter.currentAudioInput,
          videoinput: presenter.currentVideoInput,
        });
      }
    },

    async setCurrentAudioOutput(deviceId: string): Promise<void> {
      presenter.currentAudioOutput = deviceId;
      if (props.onChangeInitialDevicesIds) {
        props.onChangeInitialDevicesIds({
          audiooutput: presenter.currentAudioOutput,
          audioinput: presenter.currentAudioInput,
          videoinput: presenter.currentVideoInput,
        });
      }
    },

    async setCurrentVideoInput(deviceId: string, needToPublish?: boolean): Promise<void> {
      presenter.currentVideoInput = deviceId;
      if (!presenter.videoMuted && needToPublish) {
        await presenter.muteVideo();
        await presenter.unmuteVideo();
      } else if (needToPublish) {
        await presenter.unmuteVideo();
      }
      if (props.onChangeInitialDevicesIds) {
        props.onChangeInitialDevicesIds({
          audiooutput: presenter.currentAudioOutput,
          audioinput: presenter.currentAudioInput,
          videoinput: presenter.currentVideoInput,
        });
      }
    },

    async checkChangeDevices(): Promise<void> {
      const devices = await getDefaultDevices();

      if (presenter.currentAudioInput !== devices.audioinput) {
        presenter.currentAudioInput = devices.audioinput;
        if (!presenter.audioMuted) {
          await presenter.muteAudio();
          await presenter.unmuteAudio();
        }
      }

      if (presenter.currentAudioOutput !== devices.audiooutput) {
        presenter.currentAudioOutput = devices.audiooutput;
      }

      if (presenter.currentVideoInput !== devices.videoinput) {
        presenter.currentVideoInput = devices.videoinput;
        if (!presenter.videoMuted) {
          await presenter.muteVideo();
          await presenter.unmuteVideo();
        }
      }
      if (props.onChangeInitialDevicesIds) {
        props.onChangeInitialDevicesIds({
          audiooutput: presenter.currentAudioOutput,
          audioinput: presenter.currentAudioInput,
          videoinput: presenter.currentVideoInput,
        });
      }
    },

    async dispose(): Promise<void> {
      presenter.muteVideo();
      presenter.muteAudio();
      props.videoBackgroundPresenter.dispose();
    },

    async initAudio(): Promise<void> {
      if (!presenter.hasAudioPermission) {
        presenter.hasAudioPermission = await permissions.getAudioPermission(false);
      }

      await presenter.checkChangeDevices();
      await presenter.unmuteAudio();

      if (props.onMediaPermissionsChanged) {
        props.onMediaPermissionsChanged({
          audio: presenter.hasAudioPermission
            ? CallMediaPermissionValue.Accepted
            : CallMediaPermissionValue.Denied,
        });
      }
    },

    async initVideo(): Promise<void> {
      if (!presenter.hasVideoPermission) {
        presenter.hasVideoPermission = await permissions.getVideoPermission(
          presenter.currentVideoInput || undefined,
          false
        );
      }

      await presenter.checkChangeDevices();
      await presenter.unmuteVideo();

      if (props.onMediaPermissionsChanged) {
        props.onMediaPermissionsChanged({
          video: presenter.hasVideoPermission
            ? CallMediaPermissionValue.Accepted
            : CallMediaPermissionValue.Denied,
        });
      }
    },
  }));

  useEffect(() => {
    window.navigator.mediaDevices.addEventListener('devicechange', presenter.checkChangeDevices);
    presenter.initAudio();
    presenter.initVideo();
    return (): void => {
      window.navigator.mediaDevices.removeEventListener(
        'devicechange',
        presenter.checkChangeDevices
      );
      presenter.dispose();
    };
  }, []);

  return presenter;
};
